import { AGGREGATED } from '../../../../../../utils/constants/commonConstants';

export type PivotAmount = { period: string; amount: string | number };

export interface PivotRow {
    name: string | null;
    amounts?: PivotAmount[];
    amountsMap?: Record<string, string | number>;
    subRows?: any[];
}

export interface GroupRow {
    row: PivotRow;
    count: number;
    label: string;
    kind: GroupKind;
}

export interface Section {
    band: string;
    groupRows: GroupRow[];
    netLabel: string;
    netAmountsMap: Record<string, number>;
}

export interface CashflowLayout {
    sections: Section[];
    specialRows: GroupRow[];
}

/** Opening/Net Cashflow/Closing amounts, summed across accounts, keyed by period. */
export interface CashflowAggregates {
    opening: Record<string, number>;
    netCashflow: Record<string, number>;
    closing: Record<string, number>;
}

type SectionKey = 'operating' | 'investing' | 'financing';

/** Every group slot the design always renders — one per bucket the pivot can carry. */
type GroupKind =
    | 'operatingInflow'
    | 'operatingOutflow'
    | 'investingInflow'
    | 'investingOutflow'
    | 'financingInflow'
    | 'financingOutflow'
    | 'intracompany'
    | 'bounced'
    | 'unclassified';

const SECTION_BANDS: Record<SectionKey, string> = {
    operating: 'Cash From Operating',
    investing: 'Cash From Investing',
    financing: 'Cash From Financing',
};

const SECTION_NET_LABELS: Record<SectionKey, string> = {
    operating: 'Net Cash Flow from Operating',
    investing: 'Net Cash Flow from Investing',
    financing: 'Net Cash Flow from Financing',
};

const GROUP_LABELS: Record<GroupKind, string> = {
    operatingInflow: 'Operating Inflow',
    operatingOutflow: 'Operating Outflow',
    investingInflow: 'Investing Inflow',
    investingOutflow: 'Investing Outflow',
    financingInflow: 'Financing Inflow',
    financingOutflow: 'Financing Outflow',
    intracompany: 'Intracompany Transactions',
    bounced: 'Bounced Transactions',
    unclassified: 'Unclassified Transactions',
};

const SECTION_GROUPS: Record<SectionKey, [GroupKind, GroupKind]> = {
    operating: ['operatingInflow', 'operatingOutflow'],
    investing: ['investingInflow', 'investingOutflow'],
    financing: ['financingInflow', 'financingOutflow'],
};

const SPECIAL_KINDS: GroupKind[] = ['intracompany', 'bounced', 'unclassified'];

// Stable classification IDs from the bank-statement ingestion scheme.
// Source: recur-wiki/wiki/concepts/bank-statement-ingestion.md § Classification ID Scheme
// and uw constants/BankClassification.ts. Id 2 is absent from the scheme → unmapped → Unclassified.
const CLASSIFICATION_ID_TO_KIND: Record<number, GroupKind> = {
    [-1]: 'intracompany',
    1: 'financingInflow',
    3: 'operatingInflow',
    4: 'operatingOutflow',
    5: 'financingOutflow',
    6: 'investingInflow',
    7: 'investingOutflow',
    8: 'bounced',
};

/** Recover a stable classificationId from subRows (homogeneous per bucket group in uw). */
function rowClassificationId(row: PivotRow): number | null {
    const first = row.subRows?.find((s) => s?.classificationId != null);
    return first?.classificationId ?? null;
}

/** Merge incoming row amounts and subRows into an existing GroupRow (prevents money loss on collision). */
function mergeGroupRow(existing: GroupRow, incoming: PivotRow): void {
    const target = existing.row.amountsMap ?? {};
    for (const [period, value] of Object.entries(incoming.amountsMap ?? {})) {
        target[period] = parseAmount(target[period] ?? 0) + parseAmount(value);
    }
    existing.row.amountsMap = target;
    existing.row.subRows = [...(existing.row.subRows ?? []), ...(incoming.subRows ?? [])];
    existing.count = existing.row.subRows.length;
}

/**
 * Classify a pivot bucket into one of the fixed group slots by its (uppercase) name.
 * When the name alone is ambiguous (e.g. the API sends "Operating" for both inflow and
 * outflow), `isNegative` (derived from the row's net amounts sign) breaks the tie:
 * negative total → outflow slot; non-negative → inflow slot.
 * The bucket names come from UW verbatim and vary ("FINANCIAL INFLOW" is their
 * financing-inflow label); the unnamed/null group is Unclassified.
 */
function groupKindOf(name: string | null, isNegative = false): GroupKind | null {
    if (name == null) return 'unclassified';
    const upper = name.toUpperCase();
    if (upper.includes('BOUNCE')) return 'bounced';
    if (upper.includes('INTRA')) return 'intracompany';
    if (upper.includes('UNCLASSIFIED')) return 'unclassified';
    if (upper.includes('OPERATING')) {
        if (upper.includes('OUTFLOW')) return 'operatingOutflow';
        if (upper.includes('INFLOW')) return 'operatingInflow';
        return isNegative ? 'operatingOutflow' : 'operatingInflow';
    }
    if (upper.includes('INVESTING')) {
        if (upper.includes('OUTFLOW')) return 'investingOutflow';
        if (upper.includes('INFLOW')) return 'investingInflow';
        return isNegative ? 'investingOutflow' : 'investingInflow';
    }
    if (upper.includes('FINANCIAL') || upper.includes('FINANCING')) {
        if (upper.includes('OUTFLOW')) return 'financingOutflow';
        if (upper.includes('INFLOW')) return 'financingInflow';
        return isNegative ? 'financingOutflow' : 'financingInflow';
    }
    return null;
}

function subRowCount(row: PivotRow): number {
    return row.subRows?.length ?? 0;
}

/**
 * Parse a raw API amount value. Handles two wire formats:
 *   - Bucket path: `'-40,000'` (minus-prefix, commas)
 *   - Aggregate path (amountFormatted): `'(2,90,44,84,616)'` (parenthesised negative)
 */
function parseAmount(v: string | number): number {
    if (typeof v !== 'string') return Number.isFinite(v) ? v : 0;
    const isNeg = v.startsWith('(');
    const n = Number(v.replace(/[,() ]/g, ''));
    if (!Number.isFinite(n)) return 0;
    return isNeg ? -Math.abs(n) : n;
}

/** Collapse an amounts array into a period-keyed map for O(1) cell lookup. */
export function amountsToMap(amounts: PivotAmount[] = []): Record<string, string | number> {
    return amounts.reduce<Record<string, string | number>>((map, a) => {
        map[a.period] = a.amount;
        return map;
    }, {});
}

/** Sum all period values in an amountsMap, treating missing/non-numeric as 0. */
export function rowTotal(amountsMap: Record<string, string | number> = {}): number {
    return Object.values(amountsMap).reduce<number>((acc, v) => acc + parseAmount(v), 0);
}

export const EN_IN_INT = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
const EN_IN_2DP = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Format a FE-computed numeric amount for table display using en-IN grouping.
 * Rounds to the given precision (default whole rupees; no trailing decimals from float
 * arithmetic at 0 dp). Negative values are wrapped in parentheses to match the design.
 */
export function formatTableAmount(value: number, fractionDigits = 0): string {
    if (!Number.isFinite(value)) return '-';
    const factor = 10 ** fractionDigits;
    // `|| 0` normalises -0 — without it `-0 < 0` is false, so the parens branch is skipped and a bare minus leaks through.
    const rounded = Math.round(value * factor) / factor || 0;
    const fmt = fractionDigits > 0 ? EN_IN_2DP : EN_IN_INT;
    return rounded < 0 ? `(${fmt.format(-rounded)})` : fmt.format(rounded);
}

/** Scale a raw ₹ figure into the selected unit and format it: INR (divisor 1) stays whole
 *  rupees; Lakhs/Crore get exactly 2 decimals. Mirrors AuditedStatementTable.tsx:76-85. */
export function formatScaledAmount(value: number, unitDivisor: number): string {
    return formatTableAmount(value / unitDivisor, unitDivisor === 1 ? 0 : 2);
}

/** Sum a section's group-row per-period amounts (debits already negated in the pivot SQL). */
function sumGroupAmounts(groupRows: GroupRow[]): Record<string, number> {
    const totals: Record<string, number> = {};
    for (const { row } of groupRows) {
        const map = row.amountsMap ?? {};
        for (const [period, value] of Object.entries(map)) {
            totals[period] = (totals[period] ?? 0) + parseAmount(value);
        }
    }
    return totals;
}

/** A group slot with no matching pivot bucket — renders zero-filled, no sub-rows. */
function emptyGroupRow(kind: GroupKind): GroupRow {
    return { row: { name: null, amountsMap: {}, subRows: [] }, count: 0, label: GROUP_LABELS[kind], kind };
}

type AggregateKind = keyof CashflowAggregates;

/** Match a summaryData row's `classification` to an aggregate slot; case/spacing tolerant. */
function aggregateKindOf(classification: string | null): AggregateKind | null {
    if (!classification) return null;
    const upper = classification.toUpperCase();
    if (upper.includes('OPENING')) return 'opening';
    if (upper.includes('CLOSING')) return 'closing';
    if (upper.includes('NET CASH')) return 'netCashflow';
    return null;
}

/**
 * Normalize a period key to the canonical bucket-header form "MMM YY".
 * Accepts "Jul_2025" (polus-hop format) and converts to "Jul 25".
 * Canonical form ("Jul 25") and any other format pass through unchanged.
 */
export function normalizePeriodKey(period: string): string {
    if (/^[A-Z][a-z]{2}_\d{4}$/.test(period)) {
        const [mon, year] = period.split('_');
        return `${mon} ${year.slice(2)}`;
    }
    return period;
}

/**
 * Extract Opening Balance / Net Cashflow / Closing Balance from the account-summary
 * source, summed across accounts per period. The pivot carries no aggregate rows, so
 * these greyed top/bottom rows come from bankStatementData's per-account summaryData
 * (rows keyed by `classification`, amounts `{period, amountFormatted|amount}`).
 *
 * When selectedAccountNumbers is non-empty, only matching accounts are summed (client-side
 * filter for the CFB multi-select). When empty/undefined AND hasSelectedAccountIds is false,
 * falls back to the existing behaviour: use the "Aggregated" pseudo-account when present,
 * else sum all accounts. When hasSelectedAccountIds is true but selectedAccountNumbers is
 * empty, the selected accounts have no accountNumber — filter to the resolvable set (empty),
 * yielding zero totals rather than the all-accounts Aggregated fallback.
 */
export function buildCashflowAggregates(
    summaryData: any[] = [],
    selectedAccountNumbers?: string[],
    hasSelectedAccountIds = false,
): CashflowAggregates {
    let entries: any[];
    if (selectedAccountNumbers?.length) {
        const selected = new Set((selectedAccountNumbers ?? []).map(String));
        entries = summaryData.filter((a) => selected.has(String(a?.accountNumber)));
    } else if (hasSelectedAccountIds) {
        // Accounts are selected but none resolved to an accountNumber → zero contribution.
        entries = [];
    } else {
        const aggregatedEntry = summaryData.find((a) => a?.bankName?.toLowerCase() === AGGREGATED);
        entries = aggregatedEntry ? [aggregatedEntry] : summaryData;
    }

    const result: CashflowAggregates = { opening: {}, netCashflow: {}, closing: {} };
    for (const account of entries) {
        for (const row of account?.rows ?? []) {
            const kind = aggregateKindOf(row?.classification ?? null);
            if (!kind) continue;
            for (const a of row?.amounts ?? []) {
                if (a?.period == null) continue;
                const key = normalizePeriodKey(String(a.period));
                result[kind][key] = (result[kind][key] ?? 0) + parseAmount(a?.amountFormatted ?? a?.amount ?? 0);
            }
        }
    }
    // Derive netCashflow for any period where it is absent (polus drops the row).
    // Identity: Net Cashflow = Closing − Opening. Direct rows win when they do arrive.
    const allPeriods = new Set([...Object.keys(result.opening), ...Object.keys(result.closing)]);
    for (const period of allPeriods) {
        if (result.netCashflow[period] == null) {
            result.netCashflow[period] = (result.closing[period] ?? 0) - (result.opening[period] ?? 0);
        }
    }
    return result;
}

/**
 * Build the design's FIXED cashflow skeleton from the pivot rows.
 * Every section band, group row (Inflow/Outflow per section), and special row
 * (Intracompany/Bounced/Unclassified) ALWAYS renders — zero-filled when its
 * bucket is absent from the response. Per-section Net rows are FE-summed.
 * The pivot carries no aggregate rows; Opening/Net Cashflow/Closing are supplied
 * separately (see FinancialsFromBankingV2's aggregates fetch).
 */
export function buildCashflowLayout(rows: PivotRow[] = []): CashflowLayout {
    const byKind = new Map<GroupKind, GroupRow>();
    for (const row of rows) {
        const cid = rowClassificationId(row);
        let kind: GroupKind | null = cid != null ? CLASSIFICATION_ID_TO_KIND[cid] ?? null : null;
        if (!kind) {
            // Fallback: label+sign ladder (handles empty-subRow groups); never discard.
            const netSign = Object.values(row.amountsMap ?? {}).reduce((s: number, v) => s + parseAmount(v), 0) < 0;
            kind = groupKindOf(row.name ?? null, netSign) ?? 'unclassified';
        }
        if (byKind.has(kind)) {
            mergeGroupRow(byKind.get(kind)!, row);
        } else {
            byKind.set(kind, { row, count: subRowCount(row), label: GROUP_LABELS[kind], kind });
        }
    }

    const groupFor = (kind: GroupKind): GroupRow => byKind.get(kind) ?? emptyGroupRow(kind);

    const sections: Section[] = (Object.keys(SECTION_GROUPS) as SectionKey[]).map((key) => {
        const groupRows = SECTION_GROUPS[key].map(groupFor);
        return {
            band: SECTION_BANDS[key],
            groupRows,
            netLabel: SECTION_NET_LABELS[key],
            netAmountsMap: sumGroupAmounts(groupRows),
        };
    });

    const specialRows = SPECIAL_KINDS.map(groupFor);

    return { sections, specialRows };
}
